import { isAdmin, isVendor } from './roles.ts';

/**
 * A Chabad House login: a restaurant login whose venue is "Shabbat only".
 * Such a venue never takes orders (the server answers 404 for it as a
 * restaurant and isOpen() is always false), so the panel shows it only the
 * Shabbat pages and its own venue's details - no orders, past orders, menu,
 * open/closed switch or delete (Ariel, 19 Oct 2026).
 *
 * Decided once at login from /vendor/shabbat/venues and kept in localStorage,
 * so App.tsx can choose its routes synchronously, the same way it reads
 * ra_role. Like the rest of roles.ts this only decides what to render; the
 * server enforces who may do what.
 */

const KEY = 'ra_shabbat_only';

type Venue = { shabbatOnly: boolean };

/** True for a restaurant login whose venues are all Shabbat-only. Admins never. */
export const isShabbatOnlyLogin = (): boolean => {
    try {
        return isVendor() && !isAdmin() && localStorage.getItem(KEY) === 'true';
    } catch {
        return false;
    }
};

const decide = (venues: Venue[]): boolean =>
    isVendor() && !isAdmin() && venues.length > 0 && venues.every(v => v.shabbatOnly);

/**
 * Stores the decision for these venues. Returns true when it changed, so a
 * session that started before this existed (or whose venue was switched by an
 * admin since) can reload once into the right panel.
 */
export const rememberShabbatOnly = (venues: Venue[]): boolean => {
    try {
        const next = decide(venues);
        const before = localStorage.getItem(KEY) === 'true';
        if (next) localStorage.setItem(KEY, 'true');
        else localStorage.removeItem(KEY);
        return next !== before;
    } catch {
        return false;
    }
};

export const forgetShabbatOnly = () => {
    try { localStorage.removeItem(KEY); } catch { /* nothing stored */ }
};
